
import { RevIllustration } from '@ds/components/Illustration'
import isEmpty from 'lodash/isEmpty'
import { mapGetters } from 'vuex'

import Precipitation from '@core/components/Precipitation/Precipitation.vue'
import { removeLegacyAttributsFromProducts } from '@landing/utils/resultsWithAttributes'
import {
  getAdvertisingOptionsFromComponent,
  hasPaymentAdvertising,
} from '@payment'
import { ROUTES } from '@router'
import { EVENT_TYPE, PRODUCT_SOURCE_TYPE, saveProductInfos } from '@tracking'
import {
  trackLandingPage,
  trackProductClick,
  trackProductImpression,
  trackSearchAnalytics,
} from '@tracking/events'

import { algoliaToProduct, toTrackingData } from '../../utils/adapter'
import PlaceholderCardList from '../PlaceholderCardList.vue'
import { buildResultsFromAttributes } from '../ProductThumb'

import CardsList from './CardsList.vue'
import translations from './ProductResults.translations'

const OFFSET = 1

export default {
  components: {
    CardsList,
    Precipitation,
    RevIllustration,
    PlaceholderCardList,
  },

  computed: {
    translations() {
      return translations
    },

    ...mapGetters({
      buyingGuide: 'landing/buyingGuide',
      experiments: 'flags/getExperiments',
      isListView: 'landing/isListView',
      landingType: 'landing/type',
      listViewStructure: 'landing/listViewStructure',
      marketingContent: 'landing/marketingContent',
      paymentMethods: 'countryConfig/paymentMethods',
      searchQueryID: 'landingSearch/queryID',
      searchResultsData: 'landingSearch/results',
      sortDefaultValue: 'landingSearch/sortDefaultValue',
      visitorId: 'config/visitorId',
      searchIsLoading: 'landingSearch/isLoading',
      landingPageIsLoading: 'landing/isLoading',
      isProductModelLevel: 'landing/isProductModelLevel',
    }),

    isLoading() {
      return this.landingPageIsLoading || this.searchIsLoading
    },

    showAdvert() {
      return (
        this.searchResults?.length > 0 &&
        !isEmpty(this.marketingContent) &&
        ((!isEmpty(this.marketingContent.card) && !this.isListViewDisplay) ||
          (!isEmpty(this.marketingContent.list) && this.isListViewDisplay))
      )
    },

    hasEnoughProductsForBuyingGuidesOrPaymentMethodAds() {
      return (
        (this.showAdvert && this.searchResults?.length >= 10) ||
        (!this.showAdvert && this.searchResults?.length >= 5)
      )
    },

    showBuyingGuide() {
      return (
        this.hasEnoughProductsForBuyingGuidesOrPaymentMethodAds &&
        this.buyingGuide &&
        Object.entries(this.buyingGuide).length > 0
      )
    },

    showPaymentMethodAdvertising() {
      const hasBuyingGuideAndAdvert = this.showBuyingGuide && this.showAdvert

      return (
        this.hasEnoughProductsForBuyingGuidesOrPaymentMethodAds &&
        this.paymentMethods &&
        !hasBuyingGuideAndAdvert &&
        hasPaymentAdvertising(
          'search',
          this.paymentMethods,
          getAdvertisingOptionsFromComponent(this),
        )
      )
    },

    isProductLanding() {
      return this.$route.name === ROUTES.LANDING.PRODUCT
    },

    isListViewDisplay() {
      return this.isListView && this.isProductLanding
    },

    highlightedOffSet() {
      return !isEmpty(this.highlightedProduct) ? 1 : 0
    },

    trackingData() {
      const allResultsTrackingData = [this.highlightedProduct]
        .filter((product) => !isEmpty(product))
        .concat(this.searchResults)
        .map((product) => {
          return toTrackingData(product)
        })

      return allResultsTrackingData
    },

    displayAtProductModelLevel() {
      // Clean experiment PLP
      return (
        this.isProductModelLevel &&
        this.experiments.plpProductModelHPC === 'plpProductModel'
      )
    },

    refinedProducts() {
      return this.displayAtProductModelLevel
        ? removeLegacyAttributsFromProducts(
            buildResultsFromAttributes({
              attributes: this.searchResultsData,
              structure: this.listViewStructure,
            }),
            this.listViewStructure,
          )
        : buildResultsFromAttributes({
            attributes: this.searchResultsData,
            structure: this.listViewStructure,
          })
    },

    searchResults() {
      const start = this.highlightedProduct ? 1 : 0

      return this.refinedProducts
        .slice(start)
        .map((searchResult, index) => {
          const position = this.calcProductPosition(
            index,
            this.highlightedOffSet,
          )

          return algoliaToProduct(
            searchResult,
            this.$t(this.translations.months),
            this.displayAtProductModelLevel,
            position,
            PRODUCT_SOURCE_TYPE.RESULTS,
          )
        })
        .map((product, index) => {
          if (this.isProductLanding && index < 3) {
            return {
              ...product,
              isGoodDeal: true,
            }
          }

          return product
        })
    },

    highlightedProduct() {
      if (this.refinedProducts[0] && this.isProductLanding) {
        const product = this.refinedProducts[0]

        return algoliaToProduct(
          product,
          this.$t(this.translations.months),
          this.displayAtProductModelLevel,
          1,
          PRODUCT_SOURCE_TYPE.HIGHLIGHTED_PRODUCT,
        )
      }

      return undefined
    },
  },
  mounted() {
    this.track()
  },
  methods: {
    track() {
      trackLandingPage({
        type: this.landingType,
      })
    },

    getTrackingDataOfProduct(id) {
      return this.trackingData.find((track) => track.product.id === id)
    },

    impressionOnCard(product) {
      const { productId } = product
      const trackingDataProduct = this.getTrackingDataOfProduct(productId)

      if (!isEmpty(trackingDataProduct)) {
        trackProductImpression(trackingDataProduct.product)
      }
    },

    clickOnCard(product) {
      const { productId, objectID, price, reviewRating } = product

      saveProductInfos({
        id: productId,
        price: price?.amount,
        reviewRating,
        source: PRODUCT_SOURCE_TYPE.RESULTS,
      })

      const trackingDataProduct = this.getTrackingDataOfProduct(productId)

      if (!isEmpty(trackingDataProduct)) {
        trackProductClick(trackingDataProduct)
        trackSearchAnalytics({
          type: EVENT_TYPE.CLICK,
          objectId: objectID,
          searchQueryID: this.searchQueryID,
          tokenId: this.visitorId,
          position: trackingDataProduct.product.position,
          index: this.sortDefaultValue,
        })
      }
    },

    calcProductPosition(positionInArray, extraOffSet = 0) {
      return positionInArray + OFFSET + extraOffSet
    },
  },
}
